import { Component, Inject, OnDestroy } from '@angular/core'
import { FormControl } from '@angular/forms'
import { ResizeHandleState } from '@helios/generic-components'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import {
  RootState,
  selectCurrentUser,
  selectUserPolicy,
  User,
  UserPolicy,
} from '@helios/state-management'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { ToastService } from '@northfork/ng-basics'
import { ComponentContainer } from 'golden-layout'
import { Subject } from 'rxjs'
import { filter, takeUntil, takeWhile, tap } from 'rxjs/operators'
import { ApprovalsValidationComponent } from './components/approvals-validation/approvals-validation.component'
import {
  approvalNotesValidator,
  isValidRowToApprove,
  isValidRowToDeny,
  requestNotesValidationMessages,
  updateNotesValidator,
} from './models/approvals-util'
import {
  RequestDetail,
  Requests,
  RequestStatus,
  RequestUpdates,
  RestrictedActionStatusUpdateRequest,
} from './models/approvals.model'
import { ApprovalsService } from './services/approvals.service'
interface ApprovalsPanelState {
  resizeHandleState: ResizeHandleState
}
@Component({
  selector: 'hs-holiday-recon-approval',
  templateUrl: './approvals-panel.component.html',
  styleUrls: ['./approvals-panel.component.scss'],
})
export class ApprovalsPanelComponent
  extends BasePanelComponent<ApprovalsPanelState>
  implements OnDestroy
{
  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly store: Store<RootState>,
    private readonly approvalService: ApprovalsService,
    public toastService: ToastService,
    private readonly modalService: NgbModal,
  ) {
    super(container)
  }

  selectedRequest?: Requests[]
  selectedRequestDetails?: RequestDetail[]
  pendingRequestCount = 0
  pendingRequestDetailsCount = 0
  disableRequestDetailButton = true
  user: User | null = null
  userId?: number
  pendingRequestNotes = new FormControl('', approvalNotesValidator())
  pendingRequestDetailsNotes = new FormControl('', approvalNotesValidator())
  validationMessages = { requestNotesValidationMessages }
  rApproveBtnErrorMessage: Set<string> = new Set<string>()
  rdApproveBtnErrorMessage: Set<string> = new Set<string>()
  rDenyBtnErrorMessage: Set<string> = new Set<string>()
  rdDenyBtnErrorMessage: Set<string> = new Set<string>()
  approvableActionTypes: string[] = []
  isRequestLoading = false
  isRequestDetailLoading = false
  refreshRequestDetails?: RequestDetail[] = []
  refreshRequest = false
  hasMixedActionTypes = false
  selectedActionType: string | null = null
  allowDetailLevelApprovals = true
  private readonly destroySubject$ = new Subject<void>()

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  ngOnInit() {
    this.store
      .select(selectCurrentUser)
      .pipe(
        takeUntil(this.destroySubject$),
        takeWhile(user => user == null, true),
        filter((user): user is User => user != null),
        tap(user => this.onUserLogin(user)),
      )
      .subscribe()
  }

  async onUserLogin(user: User) {
    this.user = user
    this.userId = user.clearwaterData?.legacyUserId
    const userPolicy = await this.store
      .select(selectUserPolicy)
      .pipe(
        takeUntil(this.destroySubject$),
        takeWhile(policy => policy == null, true),
        filter((policy): policy is UserPolicy => policy != null),
      )
      .toPromise()

    this.approvableActionTypes = userPolicy.approvableActionTypes ?? []
  }

  private getCanApproveSelectedActionType(): boolean {
    return (
      this.selectedActionType != null &&
      this.approvableActionTypes.includes(this.selectedActionType)
    )
  }

  onRequestSelected(approvalRequest: Requests[]) {
    this.selectedRequest = approvalRequest
    if (!this.selectedRequest) {
      this.resetCount()
      this.hasMixedActionTypes = false
      this.selectedActionType = null
      this.allowDetailLevelApprovals = true
      return
    }

    const actionTypes = new Set(approvalRequest.map(r => r.actionType))
    this.hasMixedActionTypes = actionTypes.size > 1
    this.selectedActionType =
      actionTypes.size === 1 ? Array.from(actionTypes)[0] : null

    this.allowDetailLevelApprovals =
      this.selectedActionType !== 'RESTRICTED_BREAK'

    const canApproveSelectedActionType = this.getCanApproveSelectedActionType()

    this.rApproveBtnErrorMessage = isValidRowToApprove(
      canApproveSelectedActionType,
      approvalRequest,
      this.userId ?? 0,
    )

    this.rDenyBtnErrorMessage = isValidRowToDeny(
      canApproveSelectedActionType,
      approvalRequest,
      this.userId ?? 0,
    )

    this.pendingRequestCount = approvalRequest.filter(
      req => req.status == 'PENDING',
    ).length
    this.pendingRequestNotes = updateNotesValidator(
      true,
      this.pendingRequestNotes,
    )
  }

  onRequestDetailsSelected(approvalRequest: RequestDetail[]) {
    if (!approvalRequest) return
    this.selectedRequestDetails = approvalRequest
    const canApproveSelectedActionType = this.getCanApproveSelectedActionType()
    this.rdApproveBtnErrorMessage = isValidRowToApprove(
      canApproveSelectedActionType,
      approvalRequest,
      this.userId ?? 0,
    )
    this.rdDenyBtnErrorMessage = isValidRowToDeny(
      canApproveSelectedActionType,
      approvalRequest,
      this.userId ?? 0,
    )
    this.pendingRequestDetailsCount = approvalRequest.filter(
      req => req.status == 'PENDING',
    ).length

    this.pendingRequestDetailsNotes = updateNotesValidator(
      true,
      this.pendingRequestDetailsNotes,
    )
  }

  setRefreshRequest(flag: boolean) {
    if (flag) {
      this.refreshRequest = true
    } else {
      this.refreshRequest = false
    }
  }

  onResizeFinished(resizeHandleState: ResizeHandleState) {
    this.setState({
      ...this.state,
      resizeHandleState,
    })
  }

  denyRequest(type: string, selectedRequest?: unknown[]) {
    if (!selectedRequest) return
    const [requestObject, notes] = this.getRequestDetailsObject(
      type,
      selectedRequest,
    )

    this.updateRequestDetails(requestObject, notes, RequestStatus.DENIED, type)
  }

  approveRequest(type: string, selectedRequest?: unknown[]) {
    if (!selectedRequest) return
    const [requestObject, notes] = this.getRequestDetailsObject(
      type,
      selectedRequest,
    )
    this.updateRequestDetails(
      requestObject,
      notes,
      RequestStatus.APPROVED,
      type,
    )
  }

  getRequestDetailsObject(type: string, selectedRequest?: unknown[]) {
    let requestObject: RequestUpdates[] = []
    let notes = ''
    if ('request' == type) {
      const selectedReq = selectedRequest as Requests[]
      notes = this.pendingRequestNotes.value
      requestObject = selectedReq.map(num => ({
        requestId: num.requestId,
      }))
    } else {
      const selectedReqDetails = selectedRequest as RequestDetail[]
      notes = this.pendingRequestDetailsNotes.value
      const requestMap = new Map<number, Set<number>>()

      selectedReqDetails.forEach(request => {
        const { requestId, accountId } = request

        if (!requestMap.has(requestId)) {
          requestMap.set(requestId, new Set())
        }
        requestMap.get(requestId)?.add(accountId)
      })

      requestObject = Array.from(requestMap.entries()).map(
        ([requestId, accountIds]) => ({
          requestId,
          accountIds: Array.from(accountIds),
        }),
      )
    }
    return [requestObject, notes] as const
  }

  updateRequestDetails(
    requestObject: RequestUpdates[],
    notes: string,
    status: string,
    type: string,
  ) {
    const request = {
      requestUpdates: requestObject,
      newStatus: status,
      reviewComment: notes,
      reviewerId: this.userId,
    } as RestrictedActionStatusUpdateRequest

    this.refreshRequest = false
    if ('request' == type) {
      this.isRequestLoading = true
    } else {
      this.isRequestDetailLoading = true
    }
    this.approvalService
      .updateRequestDetails(request)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(
        response => {
          if (response.errorMessage) {
            this.openErrorModal()
            return
          }
          this.pendingRequestNotes = new FormControl(
            '',
            approvalNotesValidator(),
          )
          this.pendingRequestDetailsNotes = new FormControl(
            '',
            approvalNotesValidator(),
          )
          if ('request' == type) {
            this.isRequestLoading = false
            this.refreshRequest = true
            this.selectedRequest = undefined
          } else {
            this.isRequestDetailLoading = false
            this.refreshRequestDetails = this.selectedRequestDetails
              ? this.selectedRequestDetails
              : undefined
          }
          if (RequestStatus.DENIED == status) {
            this.toastService.success(
              'Request has been Denied Successfully',
              undefined,
              'Request Denied',
            )
          } else {
            this.toastService.success(
              'Command submission for the approved accounts is in progress. The related runs will appear soon and can be monitored via the Job Viewer panel',
              undefined,
              'Approval Successful',
            )
          }
        },
        () => {
          this.refreshRequest = false
          this.refreshRequestDetails = undefined
          this.isRequestLoading = false
          this.isRequestDetailLoading = false
          this.toastService.error('Request command submission failed.')
        },
      )
  }

  openErrorModal() {
    const helpModal = this.modalService.open(ApprovalsValidationComponent, {
      size: 'sm',
      windowClass: 'modal-window-click-through',
      backdrop: false,
      keyboard: false,
    } as NgbModalOptions)
    helpModal.result.then(
      confirmed => {
        if (confirmed) {
          this.refreshRequest = true
          this.selectedRequest = undefined
        }
      },
      () => {},
    )
  }

  getErrorMessage(message: Set<string>) {
    return Array.from(message).join('.\n')
  }

  resetCount() {
    this.pendingRequestCount = 0
    this.pendingRequestNotes = new FormControl('', approvalNotesValidator())
    this.pendingRequestDetailsCount = 0
    this.pendingRequestDetailsNotes = new FormControl(
      '',
      approvalNotesValidator(),
    )
    this.hasMixedActionTypes = false
    this.selectedActionType = null
    this.allowDetailLevelApprovals = true
  }
}
